import { ChangeEvent, FocusEvent, useContext, useMemo, useState } from 'react';

import { Box, Button, HStack, VStack } from '@chakra-ui/react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { colorOptions } from '../../gql/carGql';
import { Deal } from '../../gql/dealGql';
import { DealType } from '../../gql/generated/graphql';

import ConfirmEditModal from '../ConfirmEditModal/ConfirmEditModal';
import MmrButton from '../DealInfoAcquisition/MmrButton';
import { carValidationReq } from '../EstimateForm/ValidationSchema';
import VehicleValuesCalculator from '../EstimateForm/VehicleValuesCalculator';
import { AutoExplodeLicensePlate } from '../EstimateForm/autoExplodeLicensePlate';
import { AutoExplodeVin } from '../EstimateForm/autoExplodeVin';
import DatePicker from '../shared/DatePicker';
import Input from '../shared/Input';
import Modal from '../shared/Modal';
import NumberInput from '../shared/NumberInput';
import Select from '../shared/Select';

import {
  countryRulesFor,
  dealCountryFor,
  isCanada,
  valuationLabelsFor,
} from '../../constants/countries';
import { isDealInOrPastFunded, isUneditable } from '../../constants/dealStates';
import { mileageFieldLabel } from '../../constants/mileage';
import { PermissionEnum } from '../../constants/permissions';
import SELECT_OPTIONS from '../../constants/selectOptions';
import { DealContext } from '../../libs/DealContext';
import { AbilityContext, ModalContext } from '../../libs/contextLib';
import { passValuesToSchema } from '../../libs/utils';

interface EditVehicleInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: Deal, setSubmitting: (isSubmitting: boolean) => void) => void;
}

const EditVehicleInfoModal = ({ isOpen, onClose, onSubmit }: EditVehicleInfoModalProps) => {
  const { deal } = useContext(DealContext);
  const abilities = useContext(AbilityContext);
  const { modals, setModal } = useContext(ModalContext);

  const [carDetailsLoading, setCarDetailsLoading] = useState(false);
  const [callVinDecoder, setCallVinDecoder] = useState(false);
  const [callLicensePlateDecoder, setCallLicensePlateDecoder] = useState(false);
  const [isLicensePlateNumberDirty, setLicensePlateNumberDirty] = useState(false);
  const [isLicensePlateStateDirty, setLicensePlateStateDirty] = useState(false);
  const [isVinDirty, setVinDirty] = useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [shouldRefetchKbb, setShouldRefetchKbb] = useState(false);

  const canEditFundedOnwards =
    abilities.has(PermissionEnum.EditFundedOnwards) && isDealInOrPastFunded(deal.state);
  const isRefi = deal.type === DealType.Refi;
  const { supportsPlateLookup } = countryRulesFor(deal.country);
  const { lendingLabel, retailLabel, showsJdPower } = valuationLabelsFor(deal.country);
  const usesCbb = isCanada(deal.country);

  const validationSchema = useMemo(
    () => Yup.object().shape({ car: carValidationReq(false, isRefi) }),
    [isRefi],
  );

  return (
    <Formik
      onSubmit={(values: Deal, { setSubmitting }: FormikHelpers<Deal>) => {
        onSubmit(values, setSubmitting);
      }}
      initialValues={deal}
      // Formik's validationSchema prop validates with an empty context; the car rules read
      // `$country`, so the deal's country goes in explicitly, as the estimate form does.
      validate={(values) =>
        passValuesToSchema(values, validationSchema, {
          ...values,
          country: dealCountryFor(values),
        })
      }
      validateOnMount
      enableReinitialize
    >
      {({ handleSubmit, values, isValid, isSubmitting, handleBlur, handleChange }) => {
        const isDisabled = canEditFundedOnwards ? !isEditing : isUneditable(values.state);

        const customHandleBlur = (
          e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLSelectElement>,
        ) => {
          handleBlur(e);

          if (
            values.car.license_plate_number &&
            values.car.license_plate_state &&
            (e.target.name === 'car.license_plate_number' ||
              e.target.name === 'car.license_plate_state') &&
            (isLicensePlateNumberDirty || isLicensePlateStateDirty)
          ) {
            setCallLicensePlateDecoder(true);
          }

          if (values.car.vin.length === 17 && e.target.name === 'car.vin') {
            setCallVinDecoder(true);
          }
        };

        const customHandleChange = (
          e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
        ) => {
          if (
            e.target.name === 'car.license_plate_number' &&
            e.target.value !== values.car.license_plate_number
          ) {
            setLicensePlateNumberDirty(true);
          }
          if (
            e.target.name === 'car.license_plate_state' &&
            e.target.value !== values.car.license_plate_state
          ) {
            setLicensePlateStateDirty(true);
          }
          if (e.target.name === 'car.vin' && e.target.value !== values.car.vin) {
            setVinDirty(true);
          }

          handleChange(e);
        };

        return (
          <Modal
            title="Edit Vehicle Info"
            variant="noMargin"
            isOpen={isOpen}
            onClose={() => {
              setIsEditing(false);
              onClose();
            }}
            closeOnEsc={false}
            closeOnOverlayClick={false}
            hasForm
            formProps={{
              noValidate: true,
              onSubmit: handleSubmit,
            }}
            leftButtons={
              <Button
                variant="warning"
                onClick={() => {
                  setIsEditing(false);
                  onClose();
                }}
              >
                Cancel
              </Button>
            }
            rightButtons={
              <>
                {deal.type === DealType.Acquisition ? <MmrButton /> : null}
                <Button
                  variant="secondary"
                  hidden={!canEditFundedOnwards || isEditing}
                  onClick={() => {
                    setModal({ ConfirmEdit: true });
                  }}
                >
                  Edit
                </Button>
                <ConfirmEditModal
                  isOpen={modals.ConfirmEdit}
                  onConfirm={() => {
                    setIsEditing(true);
                  }}
                />
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText="SAVE"
                  isDisabled={!isValid}
                  hidden={canEditFundedOnwards && !isEditing}
                  onClick={() => {
                    setIsEditing(false);
                  }}
                >
                  SAVE
                </Button>
              </>
            }
          >
            <Box maxH="70vh">
              <VStack w="full" h="full" pb={2}>
                {supportsPlateLookup ? (
                  <HStack w="full">
                    <Input
                      label="License Plate Number"
                      name="car.license_plate_number"
                      onChange={customHandleChange}
                      onBlur={customHandleBlur}
                      isDisabled={isDisabled}
                    />
                    <Select
                      label="State"
                      name="car.license_plate_state"
                      options={SELECT_OPTIONS.US_STATES}
                      onChange={customHandleChange}
                      onBlur={customHandleBlur}
                      isDisabled={isDisabled}
                    />
                  </HStack>
                ) : null}

                <Input
                  label="VIN"
                  name="car.vin"
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    customHandleChange(e);
                    setShouldRefetchKbb(true);
                  }}
                  onBlur={customHandleBlur}
                  isDisabled={isDisabled}
                />

                <HStack w="full">
                  <NumberInput
                    label="Year"
                    name="car.year"
                    isDisabled={isDisabled}
                    isWhole
                    valueIsNumericString
                    isLoading={carDetailsLoading}
                  />
                  <Input
                    label="Make"
                    name="car.make"
                    isDisabled={isDisabled}
                    isLoading={carDetailsLoading}
                  />
                </HStack>

                <Input
                  label="Model"
                  name="car.model"
                  isDisabled={isDisabled}
                  isLoading={carDetailsLoading}
                />

                {usesCbb ? (
                  <Input
                    label="Trim"
                    name="car.cbb_valuation.trim_name"
                    placeholder="Pick a trim through Calculate Values"
                    isDisabled
                  />
                ) : (
                  <Input label="Trim" name="car.kbb_trim_name" isDisabled={isDisabled} />
                )}

                <HStack w="full">
                  <NumberInput
                    label={mileageFieldLabel(deal.car.mileage_unit)}
                    name="car.mileage"
                    isDisabled={isDisabled}
                    showThousandSeparator
                    isWhole
                  />
                  <Select
                    label="Color"
                    name="car.color"
                    options={colorOptions}
                    isDisabled={isDisabled}
                  />
                </HStack>

                <HStack w="full">
                  <NumberInput label={lendingLabel} name="car.book_value" isMoney isWhole />
                  <NumberInput label={retailLabel} name="car.retail_book_value" isMoney isWhole />
                </HStack>
                {showsJdPower ? (
                  <HStack w="full">
                    <NumberInput
                      label="JD Power Lending"
                      name="car.jdp_adjusted_clean_trade"
                      isMoney
                      isWhole
                    />
                    <NumberInput
                      label="JD Power Retail"
                      name="car.jdp_adjusted_clean_retail"
                      isMoney
                      isWhole
                    />
                  </HStack>
                ) : null}
                <HStack w="full" display="flex" justifyContent="end">
                  <VehicleValuesCalculator
                    kbbSelectedOptions={deal.car.kbb_selected_options_object?.selected_options}
                    shouldRefetchKbb={shouldRefetchKbb}
                    setShouldRefetchKbb={setShouldRefetchKbb}
                    upsertOptions
                  />
                </HStack>

                {deal.type === DealType.Acquisition && deal.financial_info_acquisition?.mmr ? (
                  <NumberInput label="MMR" name="car.mmr" isDisabled={isDisabled} isMoney isWhole />
                ) : null}

                <DatePicker
                  topLabel="Registration Expiration"
                  name="car.registration_expiration"
                  valueFormat="dateTimeUTC"
                  showMonthDropdown
                  showYearDropdown
                  boxStyles={{ width: '100%' }}
                />
              </VStack>
            </Box>

            <AutoExplodeVin
              callVinDecoder={callVinDecoder}
              setCallVinDecoder={setCallVinDecoder}
              isVinDirty={isVinDirty}
              setVinDirty={setVinDirty}
              setCarDetailsLoading={setCarDetailsLoading}
            />
            <AutoExplodeLicensePlate
              setCallVinDecoder={setCallVinDecoder}
              callLicensePlateDecoder={callLicensePlateDecoder}
              setCallLicensePlateDecoder={setCallLicensePlateDecoder}
              isLicensePlateNumberDirty={isLicensePlateNumberDirty}
              isLicensePlateStateDirty={isLicensePlateStateDirty}
              setLicensePlateNumberDirty={setLicensePlateNumberDirty}
              setLicensePlateStateDirty={setLicensePlateStateDirty}
              setCarDetailsLoading={setCarDetailsLoading}
            />
          </Modal>
        );
      }}
    </Formik>
  );
};

export default EditVehicleInfoModal;
